import React, { useState, useEffect } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

interface ServiceStatus {
  name: string;
  status: "operational" | "checking" | "down";
  description: string;
}

function StatusBadge({ status }: { status: ServiceStatus["status"] }) {
  const config = {
    operational: { bg: "bg-emerald-500/20", text: "text-emerald-400", dot: "bg-emerald-400", label: "Operational" },
    checking: { bg: "bg-yellow-500/20", text: "text-yellow-400", dot: "bg-yellow-400", label: "Checking..." },
    down: { bg: "bg-red-500/20", text: "text-red-400", dot: "bg-red-400", label: "Down" },
  };
  const c = config[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ${status === "operational" ? "animate-pulse" : ""}`} />
      {c.label}
    </span>
  );
}

export function Status() {
  const [services, setServices] = useState<ServiceStatus[]>([
    { name: "API Server", status: "checking", description: "Core API and chat endpoints" },
    { name: "Web Application", status: "checking", description: "Portal, admin, and landing pages" },
    { name: "WebSocket", status: "checking", description: "Real-time agent communication" },
    { name: "Database", status: "checking", description: "Data storage and retrieval" },
    { name: "AI Engine", status: "checking", description: "OpenAI-powered chat responses" },
  ]);

  useEffect(() => {
    async function checkHealth() {
      try {
        const res = await fetch("/health");
        const ok = res.ok;
        setServices((prev) =>
          prev.map((s) => ({ ...s, status: ok ? "operational" : "down" } as ServiceStatus))
        );
      } catch {
        setServices((prev) =>
          prev.map((s) => ({ ...s, status: "down" } as ServiceStatus))
        );
      }
    }
    checkHealth();
  }, []);

  const allOperational = services.every((s) => s.status === "operational");

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-4">System Status</h1>

          {/* Overall status banner */}
          <div
            className={`rounded-xl p-5 mb-10 border ${
              allOperational
                ? "bg-emerald-500/5 border-emerald-500/20"
                : "bg-yellow-500/5 border-yellow-500/20"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${
                  allOperational ? "bg-emerald-400 animate-pulse" : "bg-yellow-400"
                }`}
              />
              <span className={`text-lg font-medium ${allOperational ? "text-emerald-400" : "text-yellow-400"}`}>
                {allOperational ? "All Systems Operational" : "Checking Systems..."}
              </span>
            </div>
          </div>

          {/* Service list */}
          <div className="space-y-3">
            {services.map((service) => (
              <div
                key={service.name}
                className="flex items-center justify-between p-4 rounded-xl bg-[#14121e] border border-[#2a2040]"
              >
                <div>
                  <h3 className="text-white font-medium">{service.name}</h3>
                  <p className="text-sm text-[#5a4a7a]">{service.description}</p>
                </div>
                <StatusBadge status={service.status} />
              </div>
            ))}
          </div>

          {/* Info */}
          <div className="mt-10 p-4 rounded-xl bg-[#14121e] border border-[#2a2040]">
            <p className="text-sm text-[#8a7a9a]">
              This page automatically checks the health of our services. For real-time issues or
              scheduled maintenance, contact us at{" "}
              <a href="mailto:hello.onyx@cloudforge.com.my" className="text-[#c0a0e0] hover:underline">
                hello.onyx@cloudforge.com.my
              </a>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
